import { useState } from 'react';
import { reports } from '../lib/api.js';
import { Button, Field, Modal, Notice } from '../components/ui.tsx';

/**
 * Participant reporting (concept §11).
 *
 * Kept as an ordinary dialog rather than a messenger-styled panel: it is an
 * account-level action, and the warning it carries — that whatever is written
 * here is stored unencrypted — deserves to look different from the conversation
 * around it.
 */
export function ReportDialog({
  conversationId,
  onClose,
}: {
  conversationId: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('abuse');
  const [details, setDetails] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (sent) {
    return (
      <Modal
        title="Report received"
        onClose={onClose}
        footer={
          <>
            <span className="spacer" />
            <Button variant="primary" onClick={onClose}>
              Close
            </Button>
          </>
        }
      >
        <Notice tone="success" title="Thank you">
          The report has been recorded with the conversation’s operational details.
        </Notice>
      </Modal>
    );
  }

  return (
    <Modal
      title="Report this conversation"
      description="Anything you write here is sent unencrypted and stored."
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            icon="flag"
            busy={sending}
            onClick={() => {
              setSending(true);
              reports
                .submit(conversationId, { reason, ...(details ? { details } : {}) })
                .then(() => setSent(true))
                .catch(() => setError('The report could not be sent. Try again shortly.'))
                .finally(() => setSending(false));
            }}
          >
            Send report
          </Button>
        </>
      }
    >
      <Notice tone="warn" title="The service cannot read your conversation">
        It is encrypted, so a report cannot be checked against its content. What you write below is
        stored in plain text along with operational details such as timestamps and your IP address.
      </Notice>

      <div style={{ marginTop: 16 }}>
        <Field label="Reason" htmlFor="report-reason">
          {({ id }) => (
            <select id={id} value={reason} onChange={(event) => setReason(event.target.value)}>
              <option value="abuse">Abuse</option>
              <option value="harassment">Harassment</option>
              <option value="illegal_content">Illegal content</option>
              <option value="spam">Spam</option>
              <option value="other">Other</option>
            </select>
          )}
        </Field>
        <Field label="What happened" htmlFor="report-details" hint="Optional. Stored unencrypted.">
          {({ id, describedBy }) => (
            <textarea
              id={id}
              value={details}
              aria-describedby={describedBy}
              onChange={(event) => setDetails(event.target.value)}
            />
          )}
        </Field>
      </div>

      {error ? <Notice tone="danger">{error}</Notice> : null}
    </Modal>
  );
}
